import * as v from "valibot";

import {
  MAX_MANUAL_QUOTES,
  MAX_PURCHASE_LOTS_PER_HOLDING,
  MAX_PURCHASE_LOTS_PER_PORTFOLIO,
  MAX_STORED_PORTFOLIO_HOLDINGS,
} from "./constants";
import {
  addDecimalStrings,
  decimalStringsEqual,
  type DecimalString,
} from "./decimal";
import { findHoldingIdentityConflicts } from "./identity";
import {
  HoldingV1Schema,
  LocalPortfolioRecordV1Schema,
  ManualQuoteV1Schema,
  ManualQuoteSetV1Schema,
  PortfolioDocumentV1Schema,
  PurchaseLotV1Schema,
  rfc3339TimestampToEpochMilliseconds,
  type HoldingV1,
  type LocalPortfolioRecordV1,
  type ManualQuoteV1,
  type ManualQuoteSetV1,
  type PortfolioDocumentV1,
  type PurchaseLotV1,
} from "./schema";

export type PortfolioValidationIssueCode =
  | "required"
  | "invalid_type"
  | "unknown_key"
  | "unsupported_schema_version"
  | "invalid_id"
  | "invalid_ticker"
  | "invalid_currency"
  | "invalid_decimal"
  | "invalid_date"
  | "invalid_timestamp"
  | "invalid_format"
  | "invalid_value"
  | "empty_holdings"
  | "resource_limit"
  | "identity_required"
  | "duplicate_holding_id"
  | "duplicate_lot_id"
  | "duplicate_instrument"
  | "ambiguous_instrument"
  | "lot_quantity_mismatch"
  | "quote_value_required"
  | "quote_provenance_required"
  | "duplicate_quote"
  | "portfolio_mismatch"
  | "currency_mismatch"
  | "unknown_holding"
  | "holding_not_found"
  | "invalid_clock"
  | "chronology";

export type ValidationPath = readonly (string | number)[];

export interface PortfolioValidationIssue {
  readonly code: PortfolioValidationIssueCode;
  readonly path: ValidationPath;
  /** Dot/bracket path for form libraries; root-level issues use `$`. */
  readonly fieldPath: string;
  readonly message: string;
}

export type ValidationResult<T> =
  | {
      readonly success: true;
      readonly data: T;
      readonly issues: readonly [];
    }
  | {
      readonly success: false;
      readonly issues: readonly PortfolioValidationIssue[];
    };

const DECIMAL_FIELDS = new Set([
  "farmUnit",
  "quantity",
  "averageBuyPrice",
  "unitPrice",
  "currentPrice",
  "marketValue",
  "dailyReturnPct",
  "dailyChangeAmount",
]);
const DATE_FIELDS = new Set(["firstPurchasedAt", "purchasedAt", "marketDate"]);
const TIMESTAMP_FIELDS = new Set(["createdAt", "updatedAt", "asOf"]);
const ID_FIELDS = new Set(["portfolioId", "holdingId", "lotId"]);

export function toFieldPath(path: ValidationPath): string {
  if (path.length === 0) {
    return "$";
  }

  return path.reduce<string>((result, segment) => {
    if (typeof segment === "number") {
      return `${result}[${segment}]`;
    }
    return result ? `${result}.${segment}` : segment;
  }, "");
}

export function createPortfolioValidationIssue(
  code: PortfolioValidationIssueCode,
  path: ValidationPath,
  message: string,
): PortfolioValidationIssue {
  return {
    code,
    path: [...path],
    fieldPath: toFieldPath(path),
    message,
  };
}

export function validationSuccess<T>(data: T): ValidationResult<T> {
  return { success: true, data, issues: [] };
}

export function validationFailure<T = never>(
  issues: readonly PortfolioValidationIssue[],
): ValidationResult<T> {
  return { success: false, issues: deduplicateIssues(issues) };
}

function issuePath(issue: v.BaseIssue<unknown>): (string | number)[] {
  return (issue.path ?? []).map(({ key }) =>
    typeof key === "string" || typeof key === "number" ? key : String(key),
  );
}

function structuralIssueCode(
  issue: v.BaseIssue<unknown>,
  path: ValidationPath,
): PortfolioValidationIssueCode {
  const field = path.at(-1);

  if (issue.input === undefined) {
    return "required";
  }
  if (issue.type === "strict_object") {
    return "unknown_key";
  }
  if (
    (field === "schemaVersion" || field === "storageSchemaVersion") &&
    issue.type === "literal"
  ) {
    return "unsupported_schema_version";
  }
  if (field === "currency") {
    return "invalid_currency";
  }
  if (typeof field === "string" && DECIMAL_FIELDS.has(field)) {
    return "invalid_decimal";
  }
  if (typeof field === "string" && DATE_FIELDS.has(field)) {
    return "invalid_date";
  }
  if (typeof field === "string" && TIMESTAMP_FIELDS.has(field)) {
    return "invalid_timestamp";
  }
  if (typeof field === "string" && ID_FIELDS.has(field)) {
    return "invalid_id";
  }
  if (field === "ticker") {
    return "invalid_ticker";
  }
  if (field === "holdings" && issue.type === "min_length") {
    return "empty_holdings";
  }
  if (
    (field === "holdings" ||
      field === "purchaseLots" ||
      field === "quotes") &&
    issue.type === "max_length"
  ) {
    return "resource_limit";
  }
  if (
    issue.type === "literal" ||
    issue.type === "picklist" ||
    issue.type === "variant"
  ) {
    return "invalid_value";
  }
  if (
    issue.type === "regex" ||
    issue.type === "check" ||
    issue.type === "custom" ||
    issue.type === "max_length" ||
    issue.type === "min_length"
  ) {
    return "invalid_format";
  }
  return "invalid_type";
}

function fallbackKoreanMessage(code: PortfolioValidationIssueCode): string {
  switch (code) {
    case "required":
      return "필수 값을 입력해 주세요.";
    case "unknown_key":
      return "지원하지 않는 필드가 포함되어 있습니다.";
    case "unsupported_schema_version":
      return "지원하지 않는 스키마 버전입니다.";
    case "invalid_id":
      return "식별자 형식이 올바르지 않습니다.";
    case "invalid_ticker":
      return "티커 형식이 올바르지 않습니다.";
    case "invalid_currency":
      return "지원하는 ISO 4217 통화 코드를 입력해 주세요.";
    case "invalid_decimal":
      return "소수 형식이나 범위가 올바르지 않습니다.";
    case "invalid_date":
      return "실제로 존재하는 YYYY-MM-DD 날짜를 입력해 주세요.";
    case "invalid_timestamp":
      return "시간대가 포함된 RFC 3339 날짜·시간을 입력해 주세요.";
    case "empty_holdings":
      return "보유 종목을 한 개 이상 입력해 주세요.";
    case "resource_limit":
      return "허용된 항목 수나 입력 길이를 초과했습니다.";
    case "invalid_value":
      return "지원하지 않는 값입니다.";
    case "invalid_format":
      return "입력 형식이 올바르지 않습니다.";
    default:
      return "입력 값의 형식이 올바르지 않습니다.";
  }
}

function mapStructuralIssue(
  issue: v.BaseIssue<unknown>,
): PortfolioValidationIssue {
  const path = issuePath(issue);
  const code = structuralIssueCode(issue, path);
  const message = /[가-힣]/.test(issue.message)
    ? issue.message
    : fallbackKoreanMessage(code);
  return createPortfolioValidationIssue(code, path, message);
}

function deduplicateIssues(
  issues: readonly PortfolioValidationIssue[],
): PortfolioValidationIssue[] {
  const seen = new Set<string>();
  const result: PortfolioValidationIssue[] = [];

  for (const issue of issues) {
    const key = `${issue.code}\u0000${issue.fieldPath}\u0000${issue.message}`;
    if (!seen.has(key)) {
      seen.add(key);
      result.push(issue);
    }
  }

  return result;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * Array schemas validate their children before a piped maxLength action runs.
 * This small preflight prevents a hostile payload from forcing validation of an
 * unbounded holding/lot/quote list.
 */
function preflightResourceLimits(
  input: unknown,
): PortfolioValidationIssue[] {
  const issues: PortfolioValidationIssue[] = [];

  const inspectQuoteSet = (
    value: unknown,
    basePath: ValidationPath,
  ): void => {
    const record = asRecord(value);
    if (!record || !Array.isArray(record.quotes)) {
      return;
    }
    if (record.quotes.length > MAX_MANUAL_QUOTES) {
      issues.push(
        createPortfolioValidationIssue(
          "resource_limit",
          [...basePath, "quotes"],
          `수동 시세는 ${MAX_MANUAL_QUOTES}개 이하여야 합니다.`,
        ),
      );
    }
  };

  const inspectDocument = (
    value: unknown,
    basePath: ValidationPath,
  ): void => {
    const record = asRecord(value);
    if (!record || !Array.isArray(record.holdings)) {
      return;
    }

    if (record.holdings.length > MAX_STORED_PORTFOLIO_HOLDINGS) {
      issues.push(
        createPortfolioValidationIssue(
          "resource_limit",
          [...basePath, "holdings"],
          `저장된 보유 종목은 ${MAX_STORED_PORTFOLIO_HOLDINGS}개 이하여야 합니다.`,
        ),
      );
      return;
    }

    let totalLotCount = 0;
    record.holdings.forEach((holding, holdingIndex) => {
      const holdingRecord = asRecord(holding);
      const costBasis = asRecord(holdingRecord?.costBasis);
      if (!costBasis || !Array.isArray(costBasis.purchaseLots)) {
        return;
      }

      totalLotCount += costBasis.purchaseLots.length;
      if (
        costBasis.purchaseLots.length > MAX_PURCHASE_LOTS_PER_HOLDING
      ) {
        issues.push(
          createPortfolioValidationIssue(
            "resource_limit",
            [
              ...basePath,
              "holdings",
              holdingIndex,
              "costBasis",
              "purchaseLots",
            ],
            `종목당 매수 내역은 ${MAX_PURCHASE_LOTS_PER_HOLDING}개 이하여야 합니다.`,
          ),
        );
      }
    });

    if (totalLotCount > MAX_PURCHASE_LOTS_PER_PORTFOLIO) {
      issues.push(
        createPortfolioValidationIssue(
          "resource_limit",
          [...basePath, "holdings"],
          `포트폴리오 전체 매수 내역은 ${MAX_PURCHASE_LOTS_PER_PORTFOLIO}개 이하여야 합니다.`,
        ),
      );
    }
  };

  const root = asRecord(input);
  if (!root) {
    return issues;
  }

  inspectDocument(input, []);
  inspectQuoteSet(input, []);

  if ("document" in root) {
    inspectDocument(root.document, ["document"]);
  }
  if ("manualQuotes" in root) {
    inspectQuoteSet(root.manualQuotes, ["manualQuotes"]);
  }

  // HoldingV1 can also be validated independently.
  const directCostBasis = asRecord(root.costBasis);
  if (
    directCostBasis &&
    Array.isArray(directCostBasis.purchaseLots) &&
    directCostBasis.purchaseLots.length > MAX_PURCHASE_LOTS_PER_HOLDING
  ) {
    issues.push(
      createPortfolioValidationIssue(
        "resource_limit",
        ["costBasis", "purchaseLots"],
        `종목당 매수 내역은 ${MAX_PURCHASE_LOTS_PER_HOLDING}개 이하여야 합니다.`,
      ),
    );
  }

  return deduplicateIssues(issues);
}

function validateCreatedBeforeUpdated(
  value: Pick<HoldingV1 | PortfolioDocumentV1, "createdAt" | "updatedAt">,
  basePath: ValidationPath,
): PortfolioValidationIssue[] {
  const createdAt = rfc3339TimestampToEpochMilliseconds(value.createdAt);
  const updatedAt = rfc3339TimestampToEpochMilliseconds(value.updatedAt);
  return createdAt !== null && updatedAt !== null && createdAt <= updatedAt
    ? []
    : [
        createPortfolioValidationIssue(
          "chronology",
          [...basePath, "updatedAt"],
          "수정 시각은 생성 시각보다 빠를 수 없습니다.",
        ),
      ];
}

function validateHoldingSemantics(
  holding: HoldingV1,
  basePath: ValidationPath,
): PortfolioValidationIssue[] {
  const issues: PortfolioValidationIssue[] = [];

  if (!holding.ticker && !holding.name) {
    issues.push(
      createPortfolioValidationIssue(
        "identity_required",
        basePath,
        "티커 또는 종목명 중 하나 이상을 입력해 주세요.",
      ),
    );
  }

  issues.push(...validateCreatedBeforeUpdated(holding, basePath));

  if (holding.costBasis?.mode === "lots") {
    const lotIds = new Map<string, number>();

    holding.costBasis.purchaseLots.forEach((lot, lotIndex) => {
      const previousIndex = lotIds.get(lot.lotId);
      if (previousIndex !== undefined) {
        issues.push(
          createPortfolioValidationIssue(
            "duplicate_lot_id",
            [...basePath, "costBasis", "purchaseLots", lotIndex, "lotId"],
            `${previousIndex + 1}번째 매수 내역과 lotId가 중복되었습니다.`,
          ),
        );
      } else {
        lotIds.set(lot.lotId, lotIndex);
      }
    });

    let lotQuantityMatches: boolean;
    try {
      const lotQuantity = addDecimalStrings(
        holding.costBasis.purchaseLots.map(({ quantity }) => quantity),
      );
      lotQuantityMatches = decimalStringsEqual(
        lotQuantity,
        holding.quantity as DecimalString,
      );
    } catch {
      lotQuantityMatches = false;
    }

    if (!lotQuantityMatches) {
      issues.push(
        createPortfolioValidationIssue(
          "lot_quantity_mismatch",
          [...basePath, "costBasis", "purchaseLots"],
          "매수 내역의 수량 합계가 보유 수량과 정확히 일치해야 합니다.",
        ),
      );
    }
  }

  return issues;
}

function validatePortfolioSemantics(
  document: PortfolioDocumentV1,
  basePath: ValidationPath = [],
): PortfolioValidationIssue[] {
  const issues = validateCreatedBeforeUpdated(document, basePath);
  let totalLotCount = 0;

  document.holdings.forEach((holding, holdingIndex) => {
    const holdingPath = [...basePath, "holdings", holdingIndex] as const;
    issues.push(...validateHoldingSemantics(holding, holdingPath));
    if (holding.costBasis?.mode === "lots") {
      totalLotCount += holding.costBasis.purchaseLots.length;
    }
  });

  if (totalLotCount > MAX_PURCHASE_LOTS_PER_PORTFOLIO) {
    issues.push(
      createPortfolioValidationIssue(
        "resource_limit",
        [...basePath, "holdings"],
        `포트폴리오 전체 매수 내역은 ${MAX_PURCHASE_LOTS_PER_PORTFOLIO}개 이하여야 합니다.`,
      ),
    );
  }

  for (const conflict of findHoldingIdentityConflicts(document.holdings)) {
    const field =
      conflict.code === "duplicate_holding_id"
        ? "holdingId"
        : conflict.ticker
          ? "ticker"
          : "name";
    const path = [
      ...basePath,
      "holdings",
      conflict.rightIndex,
      field,
    ] as const;

    if (conflict.code === "duplicate_holding_id") {
      issues.push(
        createPortfolioValidationIssue(
          conflict.code,
          path,
          `${conflict.leftIndex + 1}번째 종목과 holdingId가 중복되었습니다.`,
        ),
      );
    } else if (conflict.code === "duplicate_instrument") {
      issues.push(
        createPortfolioValidationIssue(
          conflict.code,
          path,
          `${conflict.ticker ?? "종목"}이 같은 시장·거래소에 중복 등록되었습니다.`,
        ),
      );
    } else {
      issues.push(
        createPortfolioValidationIssue(
          conflict.code,
          path,
          conflict.ticker
            ? `${conflict.ticker}의 시장·거래소가 없어 기존 종목과 구분할 수 없습니다.`
            : "같은 이름의 임시 종목을 구분할 수 없습니다.",
        ),
      );
    }
  }

  return issues;
}

function validateManualQuoteSemantics(
  quoteSet: ManualQuoteSetV1,
  basePath: ValidationPath = [],
): PortfolioValidationIssue[] {
  const issues: PortfolioValidationIssue[] = [];
  const quoteHoldingIds = new Map<string, number>();

  quoteSet.quotes.forEach((quote, index) => {
    const quotePath = [...basePath, "quotes", index] as const;
    issues.push(...validateManualQuoteValue(quote, quotePath));

    const previousIndex = quoteHoldingIds.get(quote.holdingId);
    if (previousIndex !== undefined) {
      issues.push(
        createPortfolioValidationIssue(
          "duplicate_quote",
          [...quotePath, "holdingId"],
          `${previousIndex + 1}번째 시세와 holdingId가 중복되었습니다.`,
        ),
      );
    } else {
      quoteHoldingIds.set(quote.holdingId, index);
    }
  });

  return issues;
}

function validateManualQuoteValue(
  quote: ManualQuoteV1,
  basePath: ValidationPath,
): PortfolioValidationIssue[] {
  const issues =
    (quote.quoteStatus === "valid" || quote.quoteStatus === "stale") &&
    quote.currentPrice === undefined &&
    quote.marketValue === undefined
    ? [
        createPortfolioValidationIssue(
          "quote_value_required",
          [...basePath, "currentPrice"],
          "valid·stale 시세에는 현재가 또는 평가액이 필요합니다.",
        ),
      ]
    : [];

  if (quote.source !== "yahoo-finance") {
    return issues;
  }

  const requiredProviderFields = [
    "observedAt",
    "marketDate",
    "marketTimeZone",
    "providerSymbol",
    "sourceCurrency",
    "valuationCurrency",
    "currentPrice",
    "marketValue",
    "dailyReturnPct",
    "dailyChangeAmount",
  ] as const;
  for (const field of requiredProviderFields) {
    if (quote[field] === undefined) {
      issues.push(
        createPortfolioValidationIssue(
          "quote_provenance_required",
          [...basePath, field],
          "자동 조회 시세에는 값과 조회 출처 정보가 모두 필요합니다.",
        ),
      );
    }
  }
  return issues;
}

function parseStructurally<T>(
  schema: v.GenericSchema<unknown, unknown>,
  input: unknown,
): ValidationResult<T> {
  const preflightIssues = preflightResourceLimits(input);
  if (preflightIssues.length > 0) {
    return validationFailure(preflightIssues);
  }

  const parsed = v.safeParse(schema, input, {
    abortEarly: false,
    abortPipeEarly: false,
  });

  if (!parsed.success) {
    return validationFailure(parsed.issues.map(mapStructuralIssue));
  }

  return validationSuccess(parsed.output as T);
}

export function validatePurchaseLotV1(
  input: unknown,
): ValidationResult<PurchaseLotV1> {
  return parseStructurally(PurchaseLotV1Schema, input);
}

export function validateManualQuoteV1(
  input: unknown,
): ValidationResult<ManualQuoteV1> {
  const parsed = parseStructurally<ManualQuoteV1>(
    ManualQuoteV1Schema,
    input,
  );
  if (!parsed.success) {
    return parsed;
  }

  const issues = validateManualQuoteValue(parsed.data, []);
  return issues.length > 0 ? validationFailure(issues) : parsed;
}

export function validateHoldingV1(
  input: unknown,
): ValidationResult<HoldingV1> {
  const parsed = parseStructurally<HoldingV1>(HoldingV1Schema, input);
  if (!parsed.success) {
    return parsed;
  }

  const issues = validateHoldingSemantics(parsed.data, []);
  return issues.length > 0 ? validationFailure(issues) : parsed;
}

export function validatePortfolioDocumentV1(
  input: unknown,
): ValidationResult<PortfolioDocumentV1> {
  const parsed = parseStructurally<PortfolioDocumentV1>(
    PortfolioDocumentV1Schema,
    input,
  );
  if (!parsed.success) {
    return parsed;
  }

  const issues = validatePortfolioSemantics(parsed.data);
  return issues.length > 0 ? validationFailure(issues) : parsed;
}

export function validateManualQuoteSetV1(
  input: unknown,
): ValidationResult<ManualQuoteSetV1> {
  const parsed = parseStructurally<ManualQuoteSetV1>(
    ManualQuoteSetV1Schema,
    input,
  );
  if (!parsed.success) {
    return parsed;
  }

  const issues = validateManualQuoteSemantics(parsed.data);
  return issues.length > 0 ? validationFailure(issues) : parsed;
}

export function validateLocalPortfolioRecordV1(
  input: unknown,
): ValidationResult<LocalPortfolioRecordV1> {
  const parsed = parseStructurally<LocalPortfolioRecordV1>(
    LocalPortfolioRecordV1Schema,
    input,
  );
  if (!parsed.success) {
    return parsed;
  }

  const issues = [
    ...validatePortfolioSemantics(parsed.data.document, ["document"]),
  ];
  const quoteSet = parsed.data.manualQuotes;

  if (quoteSet) {
    issues.push(...validateManualQuoteSemantics(quoteSet, ["manualQuotes"]));

    if (quoteSet.portfolioId !== parsed.data.document.portfolioId) {
      issues.push(
        createPortfolioValidationIssue(
          "portfolio_mismatch",
          ["manualQuotes", "portfolioId"],
          "수동 시세의 portfolioId가 포트폴리오 문서와 일치해야 합니다.",
        ),
      );
    }

    if (quoteSet.currency !== parsed.data.document.currency) {
      issues.push(
        createPortfolioValidationIssue(
          "currency_mismatch",
          ["manualQuotes", "currency"],
          "수동 시세 통화가 포트폴리오 기준 통화와 일치해야 합니다.",
        ),
      );
    }

    const holdingIds = new Set(
      parsed.data.document.holdings.map(({ holdingId }) => holdingId),
    );
    quoteSet.quotes.forEach((quote, index) => {
      if (!holdingIds.has(quote.holdingId)) {
        issues.push(
          createPortfolioValidationIssue(
            "unknown_holding",
            ["manualQuotes", "quotes", index, "holdingId"],
            "포트폴리오에 없는 holdingId의 시세입니다.",
          ),
        );
      }
    });
  }

  return issues.length > 0 ? validationFailure(issues) : parsed;
}
